import React from "react";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import { colors } from "utils";
import { formatDuration, formatElapsedSoFar, NO_VALUE } from "./durations";
import { formatEasternMinute } from "./easternTime";
import { CRITICAL_RED } from "./styles";

const SHORT_ID_LENGTH = 8;
const MS_PER_SECOND = 1000;

const TONES = {
  good: { color: "#007A3F", background: "#E8F5EE" },
  warning: { color: "#B45309", background: "#FEF3E2" },
  critical: { color: CRITICAL_RED, background: "#FDECEC" },
  active: { color: "#1D4ED8", background: "#E8EEFD" },
  muted: { color: colors.milkGlass950, background: "#F3F4F6" }
};

export const STATUSES = {
  completed: { label: "Completed", tone: TONES.good },
  processing: { label: "Processing", tone: TONES.active },
  failed: { label: "Failed", tone: TONES.critical },
  not_started: { label: "Not started", tone: TONES.muted }
};

const Tag = styled.span`
  background: ${(p) => p.$tone.background};
  border-radius: 999px;
  color: ${(p) => p.$tone.color};
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  margin-right: 4px;
  padding: 2px 10px;
`;

const SessionId = styled.button`
  background: none;
  border: 0;
  color: ${colors.milkGlass950};
  cursor: copy;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 12px;
  padding: 0;
`;

const StartFailure = styled.div`
  color: ${CRITICAL_RED};
  font-size: 12px;
  margin-top: 4px;
`;

const secondsBetween = (earlier, later) =>
  Math.floor((new Date(later).getTime() - new Date(earlier).getTime()) / MS_PER_SECOND);

const waitText = (session, asOf) => {
  if (session.wait_seconds !== null) return formatDuration(session.wait_seconds);
  if (session.status === "processing") return formatElapsedSoFar(secondsBetween(session.ended_at, asOf));
  if (session.status === "failed") return "no report";
  return NO_VALUE;
};

const reportCountText = (session) => (session.status === "not_started" ? NO_VALUE : String(session.report_count));

const teacherText = (session) => session.teacher_name || session.teacher_email || "Unknown teacher";

const clientText = (session) => session.client_name || session.school_name || NO_VALUE;

const StatusTags = ({ session }) => {
  const status = STATUSES[session.status];
  return (
    <>
      <Tag $tone={status.tone} title={session.status_detail || undefined}>
        {status.label}
      </Tag>
      {session.is_late && <Tag $tone={TONES.warning}>Late</Tag>}
      {session.is_stuck && <Tag $tone={TONES.critical}>Stuck</Tag>}
    </>
  );
};

const SessionIdCell = ({ sessionId }) => (
  <SessionId type="button" title={sessionId} onClick={() => navigator.clipboard.writeText(sessionId)}>
    {sessionId.slice(0, SHORT_ID_LENGTH)}…
  </SessionId>
);

const needsReport = (session) => session.status === "failed" || session.is_stuck;

const GenerateButton = ({ session, isStarting, failure, onGenerate }) => {
  if (session.status === "not_started") return null;
  const isBeingGenerated = session.status === "processing" && !session.is_stuck;
  return (
    <>
      <Button
        size="sm"
        variant={needsReport(session) ? "primary" : "outline-secondary"}
        disabled={isStarting || isBeingGenerated}
        onClick={() => onGenerate(session)}>
        {isStarting ? "Starting…" : "Generate report"}
      </Button>
      {failure && <StartFailure>{failure}</StartFailure>}
    </>
  );
};

export const SESSION_COLUMNS = [
  { id: "status", header: "Status", cell: ({ row }) => <StatusTags session={row.original} /> },
  {
    id: "ended",
    header: "Session ended (ET)",
    accessorFn: (session) => new Date(session.ended_at).getTime(),
    enableSorting: true,
    cell: ({ row }) => formatEasternMinute(row.original.ended_at)
  },
  {
    id: "wait",
    header: "User Wait",
    accessorFn: (session) => session.wait_seconds ?? undefined,
    enableSorting: true,
    sortUndefined: "last",
    cell: ({ row, table }) => waitText(row.original, table.options.meta.asOf)
  },
  {
    id: "generation",
    header: "Report Generation",
    accessorFn: (session) => session.report_generation_seconds ?? undefined,
    enableSorting: true,
    sortUndefined: "last",
    cell: ({ row }) => formatDuration(row.original.report_generation_seconds)
  },
  {
    id: "reportCount",
    header: "Report Count",
    accessorFn: (session) => (session.status === "not_started" ? undefined : session.report_count),
    enableSorting: true,
    sortUndefined: "last",
    cell: ({ row }) => reportCountText(row.original)
  },
  { id: "teacher", header: "Teacher", cell: ({ row }) => teacherText(row.original) },
  { id: "client", header: "Client", cell: ({ row }) => clientText(row.original) },
  { id: "session", header: "Session", cell: ({ row }) => <SessionIdCell sessionId={row.original.session_id} /> },
  {
    id: "generate",
    header: "",
    meta: { align: "right" },
    cell: ({ row, table }) => (
      <GenerateButton
        session={row.original}
        isStarting={table.options.meta.startingIds.includes(row.id)}
        failure={table.options.meta.startFailures[row.id]}
        onGenerate={table.options.meta.onGenerate}
      />
    )
  }
];
